import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr } from "../../api";
import { useAuth } from "../../context/AuthContext";
import Seo from "../../components/Seo";
import { KeyRound } from "lucide-react";
import { OverviewTab, UsersTab, JobsTab, DisputesTab, DocumentsTab, FeesTab, ReportsTab, AuditTab, SettingsTab } from "./tabs1";
import { ImportsTab } from "./ImportsTab";
import { AnalyticsTab } from "./AnalyticsTab";
import { PromosTab, FinancesTab } from "./PromoFinTabs";
import { CmsTab, EmailsTab, BillingTab } from "./CmsBillingEmails";
import { MarketingTab } from "./MarketingTab";
import TourTab from "./TourTab";

function PinGate({ children }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/auth/pin/status").then((r) => setStatus(r.data)).catch(() => setStatus({ required: false, unlocked: true }));
  }, []);
  useEffect(() => { if (user?.role === "admin") load(); }, [user?.role, load]);

  if (user?.role !== "admin" || !status || !status.required || status.unlocked) return children;

  const setup = async (e) => {
    e.preventDefault();
    setError("");
    if (pin !== pin2) { setError("PINs don't match"); return; }
    setBusy(true);
    try { await api.post("/auth/pin/setup", { pin }); toast.success("PIN set — you're in"); setPin(""); setPin2(""); load(); }
    catch (err) { setError(fmtErr(err)); }
    setBusy(false);
  };
  const verify = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { await api.post("/auth/pin/verify", { pin }); setPin(""); load(); }
    catch (err) { setError(fmtErr(err)); }
    setBusy(false);
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6" data-testid="ct-pin-banner">
        <div className="bg-[#1C1B1A] text-white rounded-[28px] px-5 py-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
            <div className="flex items-center gap-3">
              <KeyRound className="w-5 h-5 text-[#F2D06B] shrink-0" />
              <p className="text-xs leading-relaxed">
                <span className="font-semibold">{status.pin_set ? "Extra security is on." : "Secure the Control Tower."}</span>{" "}
                <span className="text-white/70">{status.pin_set
                  ? "Enter your PIN for this session."
                  : "Set a quick 4–8 digit PIN — no authenticator apps, no hassle."}</span>
              </p>
            </div>
            {status.pin_set ? (
              <form onSubmit={verify} className="flex items-center gap-2">
                <input data-testid="ct-pin-input" type="password" inputMode="numeric" maxLength={8} required placeholder="PIN"
                  value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  className="cr-input !w-28 text-center tracking-[0.4em] font-mono" />
                <button data-testid="ct-pin-unlock-btn" disabled={busy}
                  className="bg-[#F2D06B] hover:bg-[#EFCB68] text-[#1C1B1A] px-4 py-2 rounded-full text-xs font-semibold transition-all whitespace-nowrap disabled:opacity-40">
                  {busy ? "Checking…" : "Unlock"}
                </button>
              </form>
            ) : (
              <form onSubmit={setup} className="flex items-center gap-2 flex-wrap">
                <input data-testid="ct-pin-new" type="password" inputMode="numeric" maxLength={8} required placeholder="4–8 digit PIN"
                  value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                  className="cr-input !w-36 text-center tracking-[0.3em] font-mono" />
                <input data-testid="ct-pin-confirm" type="password" inputMode="numeric" maxLength={8} required placeholder="Repeat PIN"
                  value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, ""))}
                  className="cr-input !w-36 text-center tracking-[0.3em] font-mono" />
                <button data-testid="ct-pin-set-btn" disabled={busy}
                  className="bg-[#F2D06B] hover:bg-[#EFCB68] text-[#1C1B1A] px-4 py-2 rounded-full text-xs font-semibold transition-all whitespace-nowrap disabled:opacity-40">
                  {busy ? "Saving…" : "Set PIN"}
                </button>
              </form>
            )}
          </div>
          {error && <p data-testid="ct-pin-error" className="text-xs text-red-300 mt-2">{error}</p>}
        </div>
      </div>
      {children}
    </>
  );
}

const TABS = [
  ["overview", "Overview"],
  ["analytics", "Analytics"],
  ["users", "Users"],
  ["jobs", "Jobs"],
  ["imports", "Imports & sources"],
  ["disputes", "Disputes"],
  ["documents", "Documents"],
  ["fees", "Fees"],
  ["promos", "Promos"],
  ["finances", "Finances"],
  ["billing", "Billing & plans"],
  ["emails", "Emails & rules"],
  ["inbox", "Inbox & marketing"],
  ["cms", "CMS & pages"],
  ["tour", "Platform tour"],
  ["reports", "Reports"],
  ["audit", "Audit log"],
  ["settings", "Settings"],
];

export default function ControlTower() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "overview";
  const setTab = (t) => setParams({ tab: t });
  const GROUPS = [
    ["Overview", ["overview", "analytics"]],
    ["People", ["users", "documents"]],
    ["Operations", ["jobs", "imports", "disputes"]],
    ["Money", ["fees", "promos", "finances", "billing"]],
    ["Comms", ["emails", "inbox"]],
    ["System", ["cms", "tour", "reports", "audit", "settings"]],
  ];
  const labelFor = (id) => TABS.find(([t]) => t === id)?.[1] || id;
  return (
    <PinGate>
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8" data-testid="control-tower">
      <Seo title="Control Tower | CourierRadar" noindex />
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Control Tower</h1>
      </div>
      <div className="md:hidden flex gap-1 bg-[#EFECE2] p-1.5 rounded-full mb-6 overflow-x-auto">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`ct-tab-${id}`} onClick={() => setTab(id)}
            className={`px-3.5 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${tab === id ? "bg-[#1C1B1A] text-white" : "text-[#78756E]"}`}>
            {label}
          </button>
        ))}
      </div>
      <div className="flex gap-6">
        <aside className="hidden md:block w-44 shrink-0 sticky top-20 self-start max-h-[calc(100vh-6rem)] overflow-y-auto cr-scroll" data-testid="ct-sidebar">
          {GROUPS.map(([g, ids]) => (
            <div key={g} className="mb-5">
              <p className="cr-label mb-1.5 px-3">{g}</p>
              {ids.map((id) => (
                <button key={id} data-testid={`ct-tab-${id}`} onClick={() => setTab(id)}
                  className={`block w-full text-left px-3 py-1.5 rounded-full text-xs font-medium transition-all mb-0.5 ${tab === id ? "bg-[#1C1B1A] text-white" : "text-[#78756E] hover:text-[#1C1B1A] hover:bg-[#F6F3EA]"}`}>
                  {labelFor(id)}
                </button>
              ))}
            </div>
          ))}
        </aside>
        <div className="flex-1 min-w-0">
          {tab === "overview" && <OverviewTab />}
          {tab === "analytics" && <AnalyticsTab />}
          {tab === "users" && <UsersTab />}
          {tab === "jobs" && <JobsTab />}
          {tab === "imports" && <ImportsTab />}
          {tab === "disputes" && <DisputesTab />}
          {tab === "documents" && <DocumentsTab />}
          {tab === "fees" && <FeesTab />}
          {tab === "promos" && <PromosTab />}
          {tab === "finances" && <FinancesTab />}
          {tab === "billing" && <BillingTab />}
          {tab === "emails" && <EmailsTab />}
          {tab === "inbox" && <MarketingTab />}
          {tab === "cms" && <CmsTab />}
          {tab === "tour" && <TourTab />}
          {tab === "reports" && <ReportsTab />}
          {tab === "audit" && <AuditTab />}
          {tab === "settings" && <SettingsTab />}
        </div>
      </div>
    </div>
    </PinGate>
  );
}
